import { Button, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LuDownload } from 'react-icons/lu';

import { Table } from 'components/Table';
import { TextM } from 'components/Text';
import { normalizeCpfCnpj } from 'helpers/normalizers';

import { DestinationImportRecord, ImportRecordStatus } from '../types';

export const DESTINATION_PAGE_TITLE = 'Cadastrar contas beneficiárias em lote';

const recordStatusLabels: Record<ImportRecordStatus, string> = {
	[ImportRecordStatus.SUCCESS]: 'Sucesso',
	[ImportRecordStatus.ERROR]: 'Erro',
};

interface DestinationImportTableProps {
	records: DestinationImportRecord[];
	isLoading: boolean;
}

export const DestinationImportTable = ({
	records,
	isLoading,
}: DestinationImportTableProps) => {
	const columns: ColumnsType<DestinationImportRecord> = [
		{
			title: 'Documento',
			key: 'documento',
			render: (record: DestinationImportRecord) => (
				<TextM weight="bold">
					{record.data.documento
						? normalizeCpfCnpj(record.data.documento)
						: '-'}
				</TextM>
			),
			width: '10%',
		},
		{
			title: 'Nome do beneficiário',
			key: 'nome_do_beneficiario',
			render: (record: DestinationImportRecord) => (
				<>
					{record.data.nome_do_beneficiario ? (
						<Tooltip title={record.data.nome_do_beneficiario}>
							<TextM
								weight="bold"
								style={{
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									maxWidth: '150px',
								}}
							>
								{record.data.nome_do_beneficiario}
							</TextM>
						</Tooltip>
					) : (
						<TextM weight="bold">-</TextM>
					)}
				</>
			),
			width: '10%',
		},
		{
			title: 'Tipo de Movimentação',
			key: 'tipo_de_movimentacao',
			render: (record: DestinationImportRecord) => (
				<TextM weight="bold">
					{record.data.tipo_de_movimentacao ?? '-'}
				</TextM>
			),
			width: '12%',
		},
		{
			title: 'Tipo de chave',
			key: 'tipo_de_chave',
			render: (record: DestinationImportRecord) => (
				<TextM weight="normal">
					{record.data.tipo_de_chave ?? '-'}
				</TextM>
			),
			width: '10%',
		},
		{
			title: 'Chave Pix',
			key: 'chave_pix',
			render: (record: DestinationImportRecord) => (
				<TextM weight="bold">{record.data.chave_pix ?? '-'}</TextM>
			),
			width: '10%',
		},
		{
			title: 'Tipo de Conta',
			key: 'tipo_de_conta',
			render: (record: DestinationImportRecord) => (
				<TextM weight="normal">
					{record.data.tipo_de_conta ?? '-'}
				</TextM>
			),
			width: '10%',
		},
		{
			title: 'ISPB',
			key: 'ispb',
			render: (record: DestinationImportRecord) => (
				<>
					{record.data.ispb ? (
						<Tooltip title={record.data.ispb}>
							<TextM
								weight="normal"
								style={{
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									maxWidth: '150px',
								}}
							>
								{record.data.ispb}
							</TextM>
						</Tooltip>
					) : (
						<TextM weight="normal">-</TextM>
					)}
				</>
			),
			width: '8%',
		},
		{
			title: 'Agência',
			key: 'agencia',
			render: (record: DestinationImportRecord) => (
				<TextM weight="normal">
					{record.data.agencia
						? String(record.data.agencia).padStart(4, '0')
						: '-'}
				</TextM>
			),
			width: '10%',
		},
		{
			title: 'Conta',
			key: 'conta',
			render: (record: DestinationImportRecord) => (
				<TextM weight="normal">{record.data.conta ?? '-'}</TextM>
			),
			width: '10%',
		},
		{
			title: 'Status',
			key: 'status',
			render: (record: DestinationImportRecord) => (
				<TextM weight="normal">
					{recordStatusLabels[record.status]}
				</TextM>
			),
			width: '10%',
		},
	];

	return (
		<Table
			rowKey={(_, index) => String(index)}
			columns={columns}
			dataSource={records}
			loading={isLoading}
			scroll={{ x: 1000, y: 420 }}
			pagination={false}
		/>
	);
};

interface DestinationDownloadActionsProps {
	onDownloadRemessa: () => void;
	onDownloadRetorno: () => void;
}

export const DestinationDownloadActions = ({
	onDownloadRemessa,
	onDownloadRetorno,
}: DestinationDownloadActionsProps) => (
	<>
		<Button
			type="default"
			onClick={onDownloadRemessa}
			icon={<LuDownload size={16} />}
			iconPosition="end"
		>
			Baixar remessa
		</Button>
		<Button
			type="primary"
			onClick={onDownloadRetorno}
			icon={<LuDownload size={16} />}
			iconPosition="end"
		>
			Baixar arquivo de retorno
		</Button>
	</>
);

interface DestinationFooterLeftProps {
	records: DestinationImportRecord[];
}

export const DestinationFooterLeft = ({
	records,
}: DestinationFooterLeftProps) => (
	<TextM weight="bold">{`Total: ${records.length}`}</TextM>
);
